import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Modal } from "react-bootstrap";
import { useSubmitCancellationRequest } from "../../api/mutations/useSubmitCancellationRequest";
import { useAppContext } from "../../Context/AppContext";
import { CANCELLATION_REASONS } from "../../types/Types";
import { useTranslation } from "react-i18next";
import { useLocalizedInputProps } from "../../hooks/useLocalizedInputProps";
import { useLiveTranslatedInput } from "../../hooks/useLiveTranslatedInput";

type Step = "form" | "confirm" | "success";

const CancelMembership: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { objectId } = useAppContext();
  const [step, setStep] = useState<Step>("form");
  const [reasonId, setReasonId] = useState<number | "">("");
  const [improvementFeedback, setImprovementFeedback] = useState("");
  const [subReason, setSubReason] = useState("");
  const [otherReasonText, setOtherReasonText] = useState("");
  const { lang: inputLang, dir: inputDir } = useLocalizedInputProps();
  const isRtl = inputDir === "rtl";

  const {
    bindLiveText: bindFeedbackLive,
    retranslateCurrent: retranslateFeedback,
    isTranslating: isFeedbackTranslating,
    messageTargetLang: feedbackTargetLang,
  } = useLiveTranslatedInput();
  const feedbackSnapshotRef = useRef("");

  useEffect(() => {
    retranslateFeedback(
      () => feedbackSnapshotRef.current || "",
      (s) => setImprovementFeedback(s)
    );
  }, [feedbackTargetLang, retranslateFeedback]);

  feedbackSnapshotRef.current = improvementFeedback;

  const reasonDetailOptions = useMemo(
    () =>
      CANCELLATION_REASONS.reduce<Record<number, string[]>>((acc, reason) => {
        if (!reason.detail) return acc;
        acc[reason.id] = reason.detail
          .split("/")
          .map((option) => option.trim())
          .filter(Boolean);
        return acc;
      }, {}),
    []
  );

  const getReasonLabel = (id: number, fallback: string) =>
    t(`membership.cancelReason.${id}.label`, { defaultValue: fallback });

  const getReasonDetailOptions = (id: number) =>
    (reasonDetailOptions[id] || []).map(
      (option, index) =>
        t(`membership.cancelReason.${id}.detail.${index}`, { defaultValue: option })
    );

  const { mutate: submitRequest, isPending } = useSubmitCancellationRequest({
    onSuccess: () => {
      setStep("success");
    },
    onError: () => {},
  });

  const isOtherReason = reasonId === 7;
  const isReasonComplete =
    reasonId !== "" &&
    (isOtherReason ? otherReasonText.trim() !== "" : subReason !== "");

  const handleRequestCancellation = () => {
    if (reasonId === "" || !objectId || !isReasonComplete) return;
    setStep("confirm");
  };

  const handleConfirm = () => {
    if (reasonId === "" || !objectId || !isReasonComplete) return;

    const mainReason = isOtherReason ? otherReasonText.trim() : subReason;
    const combinedFeedback = [mainReason, improvementFeedback.trim()]
      .filter(Boolean)
      .join(" | ");

    submitRequest({
      objectId,
      reasonId: Number(reasonId),
      improvementFeedback: combinedFeedback || undefined,
    });
  };

  const handleCancelConfirm = () => setStep("form");

  return (
    <div className="pb-4 m-0">
      <div className="card shadow-lg border-0 p-4">
        <Button
          variant="outline-secondary"
          className="mb-3 align-self-start"
          onClick={() => navigate(-1)}
        >
          {t("auth.back")}
        </Button>
        <h4 className="fw-semibold mb-4">{t("membership.cancelMembership")}</h4>

        {step === "form" && (
          <>
            <p className="text-muted mb-3">
              {t("membership.whyCancelling")}
            </p>
            <Form.Group className="mb-3">
              <Form.Select
                value={reasonId === "" ? "" : String(reasonId)}
                onChange={(e) => {
                  const value = e.target.value === "" ? "" : Number(e.target.value);
                  setReasonId(value);
                  setSubReason("");
                  setOtherReasonText("");
                }}
                required
                lang={inputLang}
                dir={inputDir}
                style={{
                  direction: inputDir,
                  textAlign: isRtl ? "right" : "left",
                }}
              >
                <option value="">{t("membership.selectReason")}</option>
                {CANCELLATION_REASONS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {getReasonLabel(r.id, r.label)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {reasonId !== "" && isOtherReason && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  {t("membership.tellReason")}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={t("membership.enterReasonPlaceholder")}
                  value={otherReasonText}
                  onChange={(e) => setOtherReasonText(e.target.value)}
                  lang={inputLang}
                  dir={inputDir}
                  style={{
                    direction: inputDir,
                    textAlign: isRtl ? "right" : "left",
                  }}
                />
              </Form.Group>
            )}
            {reasonId !== "" && !isOtherReason && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  {t("membership.tellUsMore")}
                </Form.Label>
                <div className="d-flex flex-column gap-1">
                  {getReasonDetailOptions(reasonId).map((option) => (
                      <Form.Check
                        key={option}
                        type="radio"
                        id={`sub-reason-${reasonId}-${option}`}
                        name="sub-reason"
                        label={option}
                        value={option}
                        checked={subReason === option}
                        onChange={(e) => setSubReason(e.currentTarget.value)}
                      />
                    ))}
                </div>
              </Form.Group>
            )}
            <Form.Group className="mb-4">
              <Form.Label>{t("membership.anythingImprove")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder={t("membership.optionalFeedback")}
                value={improvementFeedback}
                onChange={(e) => {
                  feedbackSnapshotRef.current = e.target.value;
                  setImprovementFeedback(e.target.value);
                  bindFeedbackLive(e.target.value, (s) =>
                    setImprovementFeedback(s)
                  );
                }}
                lang={feedbackTargetLang}
                dir={inputDir}
                style={{
                  direction: inputDir,
                  textAlign: isRtl ? "right" : "left",
                }}
              />
              {isFeedbackTranslating && (
                <Form.Text className="text-muted" aria-live="polite">
                  {t("contact.messageTranslating")}
                </Form.Text>
              )}
            </Form.Group>
            <Button
              variant="danger"
              onClick={handleRequestCancellation}
              disabled={reasonId === "" || !isReasonComplete}
            >
              {t("membership.requestCancellation")}
            </Button>
          </>
        )}

        {step === "confirm" && (
          <Modal show centered onHide={handleCancelConfirm}>
            <Modal.Header closeButton>
              <Modal.Title>{t("membership.confirmCancellation")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {t("membership.confirmCancellationPrompt")}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelConfirm}>
                {t("connections.cancel")}
              </Button>
              <Button variant="danger" onClick={handleConfirm} disabled={isPending}>
                {isPending ? t("membership.submitting") : t("common.done")}
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {step === "success" && (
          <Modal show centered onHide={() => navigate("/manage-membership")}>
            <Modal.Header closeButton>
              <Modal.Title>{t("membership.requestReceived")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {t("membership.requestReceivedBody")}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                onClick={() => navigate("/manage-membership")}
              >
                {t("common.close")}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default CancelMembership;
